import { formatInTimeZone } from 'date-fns-tz';
import type { IntlFormatters } from 'react-intl';

import { DateSalesWithTz } from '@eventbrite/event-listing-service-client';
import { getFormattedDateTime } from '@eventbrite/legacy-infra.datetime-fns';

import { BasicInfo } from '@/types';

export const DEFAULT_TZ = 'UTC';
export const FULL_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ssXXX";
const DEFAULT_LOCALE = 'en-US';

const TWELVE_HOUR_LOCALES = ['en', 'en-US', 'en-GB', 'en-IE', 'en-AU', 'en-NZ'];

export const shouldShow12hCycle = (locale: string): boolean => {
  return TWELVE_HOUR_LOCALES.includes(locale);
};

type FormatMessage = IntlFormatters['formatMessage'];

export type DateObject = {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  dayPeriod: string;
  timeZoneName: string;
};

export enum MonthFormat {
  LONG = 'long',
  SHORT = 'short',
}

export enum WeekdayFormat {
  LONG = 'long',
  SHORT = 'short',
}

export interface DateFormatOptions {
  locale: string;
  timezone: string;
  monthFormat?: MonthFormat | null;
  showYear?: boolean;
  weekday?: WeekdayFormat | null;
}

export const getAttendeeTimezone = (): string | undefined => {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e: unknown) {
    console.error('Error getting attendee timezone from browser', e);
    return undefined;
  }
};

export const getDisplayTimezone = ({
  isOnlineEvent,
  eventTimezone,
  attendeeTimezone,
}: {
  isOnlineEvent: boolean;
  eventTimezone?: string | null;
  attendeeTimezone?: string;
}): string => {
  const normalizedEventTimezone = eventTimezone?.trim() ? eventTimezone : undefined;
  const normalizedAttendeeTimezone = attendeeTimezone?.trim() ? attendeeTimezone : undefined;

  if (isOnlineEvent) {
    return normalizedAttendeeTimezone ?? normalizedEventTimezone ?? DEFAULT_TZ;
  }

  return normalizedEventTimezone ?? DEFAULT_TZ;
};

export const isSameDayInTimezone = (utcString1: string, utcString2: string, timezone: string): boolean => {
  const date1 = new Date(utcString1);
  const date2 = new Date(utcString2);

  // Convert both dates to the target timezone and compare the date parts
  const day1 = date1.toLocaleDateString(DEFAULT_LOCALE, { timeZone: timezone });
  const day2 = date2.toLocaleDateString(DEFAULT_LOCALE, { timeZone: timezone });

  return day1 === day2;
};

export const isSameMonthInTimezone = (utcString1: string, utcString2: string, timezone: string): boolean => {
  const month1 = new Date(utcString1).toLocaleString(DEFAULT_LOCALE, { timeZone: timezone, month: 'numeric' });
  const month2 = new Date(utcString2).toLocaleString(DEFAULT_LOCALE, { timeZone: timezone, month: 'numeric' });
  return month1 === month2;
};

export const isSameYearInTimezone = (utcString1: string, utcString2: string, timezone: string): boolean => {
  const year1 = new Date(utcString1).toLocaleString(DEFAULT_LOCALE, { timeZone: timezone, year: 'numeric' });
  const year2 = new Date(utcString2).toLocaleString(DEFAULT_LOCALE, { timeZone: timezone, year: 'numeric' });
  return year1 === year2;
};

export const bulletSeparator = '\u00A0•\u00A0';

const isCurrentYear = (utcString: string, locale: string, timezone: string): boolean => {
  const dateObj = new Date(utcString);
  const currentDate = new Date();

  // Get years in the specified timezone using Intl.DateTimeFormat
  const getYearInTimezone = (date: Date) => {
    const formatter = new Intl.DateTimeFormat('en', {
      year: 'numeric',
      timeZone: timezone,
    });
    return parseInt(formatter.format(date), 10);
  };

  const dateYear = getYearInTimezone(dateObj);
  const currentYear = getYearInTimezone(currentDate);

  return dateYear === currentYear;
};
const extractTimezoneFromFormatted = (formatted: string): string | null => {
  const match = formatted.match(/\s([A-Z]{2,5}(?:\+|-)\d{1,2}|[A-Z]{2,5})$/);
  return match?.[1] || null;
};

const extractTimezoneFromIntl = (utcString: string, locale: string, timezone: string): string => {
  const timeZonePart = new Intl.DateTimeFormat(locale, {
    timeZoneName: 'short',
    timeZone: timezone,
  })
    .formatToParts(new Date(utcString))
    .find((part) => part.type === 'timeZoneName');

  if (!timeZonePart?.value) {
    return '';
  }

  const abbrevMatch = timeZonePart.value.match(/[A-Z]{2,5}(?:\+|-)\d{1,2}|[A-Z]{2,5}/);
  return abbrevMatch ? abbrevMatch[0] : timeZonePart.value.toUpperCase().slice(0, 5);
};

const extractTimezoneAbbreviation = (utcString: string, locale: string, timezone: string): string => {
  const formatted = getFormattedDateTime(utcString, true, timezone, locale, 'p');
  const fromFormatted = extractTimezoneFromFormatted(formatted);

  if (fromFormatted) {
    return fromFormatted;
  }

  try {
    return extractTimezoneFromIntl(utcString, locale, timezone);
  } catch (error) {
    console.error('Error extracting timezone abbreviation', error);
    return '';
  }
};

const normalizeTimeFormat = (formatted: string): string =>
  formatted.replace(/:00\s*(AM|PM)/g, ' $1').replace(/(\d)(AM|PM)/g, '$1 $2');

const formatTimeFromParts = (hour: string, minute: string, dayPeriod: string): string => {
  // For 12-hour format (with AM/PM), omit :00 for whole hours
  // For 24-hour format (no AM/PM), always show minutes including :00
  const time = dayPeriod && minute === '00' ? hour : `${hour}:${minute}`;
  return dayPeriod ? `${time} ${dayPeriod.toUpperCase()}` : `${hour}:${minute}`;
};

const extractTimeFromFormatted = (formatted: string): string => {
  const timeMatch = formatted.match(/(\d{1,2}:\d{2}\s*(?:AM|PM|am|pm))/i);
  return timeMatch
    ? normalizeTimeFormat(timeMatch[1])
    : normalizeTimeFormat(formatted.replace(/\s([A-Z]{2,5}(?:\+|-)\d{1,2}|[A-Z]{2,5})$/, ''));
};

const formatTimeOnly = (utcString: string, locale: string, timezone: string): string => {
  const hourCycle = shouldShow12hCycle(locale) ? 'h12' : 'h23';

  try {
    const parts = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hourCycle: hourCycle,
      timeZone: timezone,
    }).formatToParts(new Date(utcString));

    const getPart = (type: string) => parts.find((p) => p.type === type)?.value || '';
    const hour = getPart('hour');
    const minute = getPart('minute');
    const dayPeriod = getPart('dayPeriod');

    if (hour && minute) {
      return formatTimeFromParts(hour, minute, dayPeriod);
    }
  } catch (error) {
    console.error('Error formatting time', error);
  }

  const formatted = getFormattedDateTime(utcString, true, timezone, locale, 'p');
  return extractTimeFromFormatted(formatted);
};

const createAtSeparator = (formatMessage: FormatMessage): string =>
  `'${formatMessage({ id: 'at', defaultMessage: 'at', description: 'Date joiner from day to hour' })}'`;

export const getFormattedDateForSingleDay = (
  date: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  includeWeekday: boolean = true,
): string => {
  if (!date.utc) {
    return '';
  }

  const addYear = !isCurrentYear(date.utc, locale, timezone);
  const atSeparator = createAtSeparator(formatMessage);
  const datePart = includeWeekday ? `eeee, MMM d` : `MMM d`;
  const format = `${datePart}${addYear ? ', yyyy' : ''} ${atSeparator} p`;

  return normalizeTimeFormat(getFormattedDateTime(date.utc, true, timezone, locale, format));
};

export const getFormattedDateOnly = (date: DateSalesWithTz, locale: string, timezone: string): string => {
  if (!date.utc) {
    return '';
  }
  const addYear = !isCurrentYear(date.utc, locale, timezone);
  return getFormattedDateTime(date.utc, false, timezone, locale, `eeee, MMM d${addYear ? ', yyyy' : ''}`);
};

const getFormattedDateWithTimeIntl = (
  date: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  dateTimeSeparator?: string,
  includeWeekday?: boolean,
  showTimezone: boolean = true,
): string => {
  if (!date.utc) return '';
  const separator = dateTimeSeparator ?? bulletSeparator;
  const startsAt = formatMessage({
    id: 'Starts at',
    defaultMessage: 'Starts at',
    description: 'Label before start time when end time is hidden',
  });
  const dateFormatted = includeWeekday
    ? formatDateWithWeekday(date, locale, timezone, true)
    : formatDateWithoutWeekday(date, locale, timezone);
  const timeFormatted = formatTimeOnly(date.utc, locale, timezone);
  const timezoneAbbrev = extractTimezoneAbbreviation(date.utc, locale, timezone);

  return normalizeTimeFormat(
    `${dateFormatted} ${separator} ${startsAt} ${timeFormatted}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`,
  );
};

export const getSingleDateDisplayString = (
  date: DateSalesWithTz,
  showTime: boolean,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  includeWeekday: boolean = true,
  dateTimeSeparator?: string,
  useAbbreviatedWeekday?: boolean,
  showTimezone: boolean = true,
): string => {
  if (!date.utc) {
    return '';
  }

  if (useAbbreviatedWeekday) {
    const datePart = formatDateWithAbbreviatedWeekday(date, locale, timezone);
    if (!showTime) return datePart;
    const separator = dateTimeSeparator ?? bulletSeparator;
    const timeFormatted = formatTimeOnly(date.utc, locale, timezone);
    const timezoneAbbrev = extractTimezoneAbbreviation(date.utc, locale, timezone);
    return normalizeTimeFormat(
      `${datePart}${separator}${timeFormatted}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`,
    );
  }
  if (showTime) {
    if (!includeWeekday) {
      return getFormattedDateWithTimeIntl(
        date,
        locale,
        timezone,
        formatMessage,
        dateTimeSeparator,
        undefined,
        showTimezone,
      );
    }
    return getFormattedDateForSingleDay(date, locale, timezone, formatMessage, includeWeekday);
  }
  return includeWeekday
    ? getFormattedDateOnly(date, locale, timezone)
    : formatDateWithoutWeekday(date, locale, timezone);
};

const formatTimezoneIfPresent = (timezone: string, show: boolean = true): string =>
  show && timezone ? ` ${timezone}` : '';

export const getFormattedDateForSameDayWithTimeRange = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  showTimezone: boolean = true,
): string => {
  if (!startDate.utc || !endDate.utc) {
    return '';
  }

  const dateFormatted = formatDateWithWeekday(startDate, locale, timezone, true);
  const timezoneAbbrev = showTimezone ? extractTimezoneAbbreviation(startDate.utc, locale, timezone) : '';

  const startTime = formatTimeOnly(startDate.utc, locale, timezone);
  const endTime = formatTimeOnly(endDate.utc, locale, timezone);

  const result = `${dateFormatted} ${bulletSeparator} ${startTime} - ${endTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;

  return conditionallyRemoveTimezone(normalizeTimeFormat(result), showTimezone);
};

const conditionallyRemoveTimezone = (str: string, showTimezone: boolean): string => {
  if (showTimezone) return str;
  return str.replace(/\s+(GMT|UTC)[\+\-]\d{1,2}|\s+(?!AM\b|PM\b)[A-Z]{2,5}(?=\s|$)/g, '');
};

export const formatDateWithoutWeekday = (date: DateSalesWithTz, locale: string, timezone: string): string => {
  if (!date.utc) return '';
  const showYear = !isCurrentYear(date.utc, locale, timezone);
  return getFormattedDateOnlyLocaleBased(new Date(date.utc), {
    locale,
    monthFormat: MonthFormat.SHORT,
    timezone,
    showYear,
    weekday: null,
  });
};

const formatDateWithWeekday = (
  date: DateSalesWithTz,
  locale: string,
  timezone: string,
  longMonth: boolean = false,
  skipYear: boolean = false,
): string => {
  if (!date.utc) return '';

  const showYear = !skipYear && !isCurrentYear(date.utc, locale, timezone);
  const monthFormat = longMonth ? MonthFormat.LONG : MonthFormat.SHORT;
  return getFormattedDateOnlyLocaleBased(new Date(date.utc), {
    locale,
    monthFormat,
    timezone,
    showYear,
    weekday: WeekdayFormat.LONG,
  });
};

export const formatDateWithAbbreviatedWeekday = (date: DateSalesWithTz, locale: string, timezone: string): string => {
  if (!date.utc) return '';

  const dateObj = new Date(date.utc);
  const addYear = !isCurrentYear(date.utc, locale, timezone);

  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(addYear && { year: 'numeric' }),
    timeZone: timezone,
  });

  return formatter.format(dateObj);
};

const getFormattedDateRangeOnly = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
): string => {
  if (!startDate.utc || !endDate.utc) return '';

  // If same day, just return the start date
  if (isSameDayInTimezone(startDate.utc, endDate.utc, timezone)) {
    const addYear = !isCurrentYear(startDate.utc, locale, timezone);
    return getFormattedDateTime(startDate.utc, false, timezone, locale, `MMM d${addYear ? ', yyyy' : ''}`);
  }

  // For date ranges in the same year, only show year on the end date if it's not the current year
  const sameYear = isSameYearInTimezone(startDate.utc, endDate.utc, timezone);
  const showYearInStart = !sameYear && !isCurrentYear(startDate.utc, locale, timezone);
  const showYearInEnd = !isCurrentYear(endDate.utc, locale, timezone);

  const startFormatted = getFormattedDateOnlyLocaleBased(new Date(startDate.utc), {
    locale,
    monthFormat: MonthFormat.LONG,
    timezone,
    showYear: showYearInStart,
    weekday: WeekdayFormat.LONG,
  });
  const endFormatted = getFormattedDateOnlyLocaleBased(new Date(endDate.utc), {
    locale,
    monthFormat: MonthFormat.LONG,
    timezone,
    showYear: showYearInEnd,
    weekday: WeekdayFormat.LONG,
  });
  return `${startFormatted}-${endFormatted}`;
};

export const getFormattedDateEndsAt = (
  date: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  showTimezone: boolean = true,
): string => {
  if (!date.utc) return '';
  const endsAt = formatMessage({
    id: 'Ends at',
    defaultMessage: 'Ends at',
    description: 'Label before end time when start time is hidden',
  });
  const dateFormatted = formatDateWithWeekday(date, locale, timezone, true);
  const endTime = formatTimeOnly(date.utc, locale, timezone);
  const timezoneAbbrev = extractTimezoneAbbreviation(date.utc, locale, timezone);
  return `${dateFormatted} ${bulletSeparator} ${endsAt} ${endTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;
};

const getFormattedDateRangeWithEndTimeOnly = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  showTimezone: boolean = true,
): string => {
  if (!startDate.utc || !endDate.utc) return '';
  const endsAt = formatMessage({
    id: 'Ends at',
    defaultMessage: 'Ends at',
    description: 'Label before end time when start time is hidden',
  });
  const skipStartYear = isSameYearInTimezone(startDate.utc, endDate.utc, timezone);
  const startFormatted = formatDateWithWeekday(startDate, locale, timezone, true, skipStartYear);
  const endFormatted = formatDateWithWeekday(endDate, locale, timezone, true);
  const endTime = formatTimeOnly(endDate.utc, locale, timezone);
  const timezoneAbbrev = extractTimezoneAbbreviation(endDate.utc, locale, timezone);
  return `${startFormatted}-${endFormatted} ${bulletSeparator} ${endsAt} ${endTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;
};

const getFormattedDateRangeWithStartTimeOnly = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  showTimezone: boolean = true,
): string => {
  if (!startDate.utc || !endDate.utc) return '';
  const startsAt = formatMessage({
    id: 'Starts at',
    defaultMessage: 'Starts at',
    description: 'Label before start time when end time is hidden',
  });
  const skipStartYear = isSameYearInTimezone(startDate.utc, endDate.utc, timezone);
  const startFormatted = formatDateWithWeekday(startDate, locale, timezone, true, skipStartYear);
  const endFormatted = formatDateWithWeekday(endDate, locale, timezone, true);
  const startTime = formatTimeOnly(startDate.utc, locale, timezone);
  const timezoneAbbrev = extractTimezoneAbbreviation(startDate.utc, locale, timezone);
  return `${startFormatted}-${endFormatted} ${bulletSeparator} ${startsAt} ${startTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;
};

export const getFormattedDateForTwoDays = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  showTimezone: boolean = true,
): string => {
  if (!startDate.utc || !endDate.utc) {
    return '';
  }

  // Use robust year comparison with Intl.DateTimeFormat
  const startDateObj = new Date(startDate.utc);
  const endDateObj = new Date(endDate.utc);

  const addYearToStart = !isSameYearInTimezone(startDate.utc, endDate.utc, timezone);
  const addYearToEnd = !isCurrentYear(endDate.utc, locale, timezone);

  // Use locale-aware formatting instead of hardcoded English format
  const startDateFormatted = getFormattedDateOnlyLocaleBased(startDateObj, {
    locale,
    monthFormat: MonthFormat.LONG,
    timezone,
    showYear: addYearToStart,
    weekday: WeekdayFormat.LONG,
  });

  const endDateFormatted = getFormattedDateOnlyLocaleBased(endDateObj, {
    locale,
    monthFormat: MonthFormat.LONG,
    timezone,
    showYear: addYearToEnd,
    weekday: WeekdayFormat.LONG,
  });

  const timezoneAbbrev = extractTimezoneAbbreviation(startDate.utc, locale, timezone);
  const startTime = formatTimeOnly(startDate.utc, locale, timezone);
  const endTime = formatTimeOnly(endDate.utc, locale, timezone);

  const result = `${startDateFormatted}-${endDateFormatted} ${bulletSeparator} ${startTime}-${endTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;
  return conditionallyRemoveTimezone(normalizeTimeFormat(result), showTimezone);
};

const EMPTY_END_DATE: DateSalesWithTz = { utc: null, timezone: undefined } as unknown as DateSalesWithTz;

export type GetEventDateTimeDisplayStringParams = {
  startDate: DateSalesWithTz;
  endDate: DateSalesWithTz;
  hideStartDate: boolean;
  hideEndDate: boolean;
  locale: string;
  timezone: string;
  formatMessage: FormatMessage;
  isRecurringEvent: boolean;
  recurringEventLabel?: string;
  showTimezone?: boolean;
};

export const getEventDateTimeDisplayString = (params: GetEventDateTimeDisplayStringParams): string | null => {
  const {
    startDate,
    endDate: rawEndDate,
    hideStartDate,
    hideEndDate,
    locale,
    timezone,
    formatMessage,
    isRecurringEvent,
    recurringEventLabel,
    showTimezone = true,
  } = params;

  const endDate = rawEndDate ?? EMPTY_END_DATE;

  if (!startDate.utc) {
    return null;
  }

  if (isRecurringEvent && recurringEventLabel) {
    return recurringEventLabel;
  }

  if (hideStartDate && hideEndDate) {
    if (endDate.utc && !isSameDayInTimezone(startDate.utc, endDate.utc, timezone)) {
      return getFormattedDateRangeOnly(startDate, endDate, locale, timezone);
    }
    return formatDateWithWeekday(startDate, locale, timezone, true);
  }

  if (!hideStartDate && hideEndDate) {
    if (endDate.utc && !isSameDayInTimezone(startDate.utc, endDate.utc, timezone)) {
      return getFormattedDateRangeWithStartTimeOnly(startDate, endDate, locale, timezone, formatMessage, showTimezone);
    }
    return getFormattedDateWithTimeIntl(startDate, locale, timezone, formatMessage, undefined, true, showTimezone);
  }

  if (hideStartDate && !hideEndDate) {
    if (!endDate.utc) {
      return formatDateWithWeekday(startDate, locale, timezone, true);
    }
    if (isSameDayInTimezone(startDate.utc, endDate.utc, timezone)) {
      return getFormattedDateEndsAt(endDate, locale, timezone, formatMessage, showTimezone);
    }
    return getFormattedDateRangeWithEndTimeOnly(startDate, endDate, locale, timezone, formatMessage, showTimezone);
  }

  if (!endDate.utc) {
    return getFormattedDateWithTimeIntl(startDate, locale, timezone, formatMessage, undefined, true, showTimezone);
  }

  if (isSameDayInTimezone(startDate.utc, endDate.utc, timezone)) {
    return getFormattedDateForSameDayWithTimeRange(startDate, endDate, locale, timezone, showTimezone);
  }

  return getFormattedDateForTwoDays(startDate, endDate, locale, timezone, showTimezone);
};

export const getFormattedStartDate = (basicInfo: BasicInfo) => {
  if (!basicInfo.hideStartDate && basicInfo.startDate.utc) {
    return formatInTimeZone(
      new Date(basicInfo.startDate.utc),
      basicInfo.startDate.timezone || DEFAULT_TZ,
      FULL_DATE_TIME_FORMAT,
    );
  }
  return null;
};

export const getFormattedEndDate = (basicInfo: BasicInfo) => {
  if (!basicInfo.hideEndDate && basicInfo.endDate?.utc) {
    return formatInTimeZone(
      new Date(basicInfo.endDate.utc),
      basicInfo.endDate.timezone || DEFAULT_TZ,
      FULL_DATE_TIME_FORMAT,
    );
  }
  return null;
};

export const getFormattedDateOnlyLocaleBased = (dateObj: Date, options: DateFormatOptions): string => {
  const { locale, timezone, monthFormat = MonthFormat.LONG, showYear = false, weekday = null } = options;

  const formatOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    timeZone: timezone,
  };

  if (monthFormat) {
    formatOptions.month = monthFormat;
  }

  if (showYear) {
    formatOptions.year = 'numeric';
  }

  if (weekday) {
    formatOptions.weekday = weekday;
  }

  const formatter = new Intl.DateTimeFormat(locale, formatOptions);
  return formatter.format(dateObj);
};

export const getSimpleFormattedDateForTwoDays = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
): string => {
  if (!startDate.utc || !endDate.utc) {
    return '';
  }

  const sameMonth = isSameMonthInTimezone(startDate.utc, endDate.utc, timezone);
  const sameYear = isSameYearInTimezone(startDate.utc, endDate.utc, timezone);

  if (sameMonth && sameYear) {
    if (isSameDayInTimezone(startDate.utc, endDate.utc, timezone)) {
      return formatDateWithoutWeekday(startDate, locale, timezone);
    }

    const baseFormatted = formatDateWithoutWeekday(startDate, locale, timezone);
    const endDay = new Date(endDate.utc).toLocaleString(DEFAULT_LOCALE, {
      timeZone: timezone,
      day: 'numeric',
    });
    // Replace the day with range, keeping the rest of the native format
    const dayPattern = /\b\d+\b/;
    return baseFormatted.replace(dayPattern, (match) => `${match}-${endDay}`);
  } else {
    // Different months: use full separate format for each date
    const startFormatted = formatDateWithoutWeekday(startDate, locale, timezone);
    const endFormatted = formatDateWithoutWeekday(endDate, locale, timezone);
    return `${startFormatted}-${endFormatted}`;
  }
};
